import {GlobalEventsMapForBackwardCompatibility} from '@gongxigames/framework/slot/events';
import {CoreDI, inject, ModuleIsEnabledFilter, Installer} from '@gongxigames/framework/core';
import {SlotSettings} from '@gongxigames/framework/slot/configs/SlotSettings';
import {OddsUpfrontReactView} from '@gongxigames/framework/slot/modules/oddsUpfront/OddsUpfrontReactView';
import {GameConfig, Module, objectHashMapToMap} from '@gongxigames/framework/slot';

export type UpfrontOddsModuleEvents = GlobalEventsMapForBackwardCompatibility & {
    'UpfrontOddsReactView.ACCEPT_CLICKED': void;
    'UpfrontOddsReactView.MODULE_INITIALIZED': void;
};

export type OddsUpfrontSymbolsData = Record<string, {
    paytable: number[];
    symbolSpriteSrc: string;
}>;

export class OddsUpfrontModule extends Module<OddsUpfrontReactView, UpfrontOddsModuleEvents> {
    public static readonly NAME: symbol = Symbol('UpfrontOddsModule');
    public static readonly isEnabled: ModuleIsEnabledFilter = (components: Installer) => {
        return components.getComponent<SlotSettings>(CoreDI.Settings).get('showOddsUpfront') || false;
    };

    @inject(CoreDI.GameConfig)
    protected readonly _gameConfig!: GameConfig;

    protected setupViews(): void {
        this.setView(OddsUpfrontReactView.NAME);
    }

    public init(): void {
        super.init();
        this.initData();
        this.onInitialized();
    }

    protected getSymbolsMap(): Map<string, string> {
        return objectHashMapToMap<string, string>(this._gameConfig.reelsConfig.symbolsSprites);
    }

    protected getSymbolsData(symbolsMap: Map<string, string>): OddsUpfrontSymbolsData {
        const symbolsData: OddsUpfrontSymbolsData = {};

        symbolsMap.forEach((symbolSpriteSrc, symbolId) => {
            let paytable = this.getPayTable(symbolId);

            if (paytable?.length && paytable.some(p => p > 0)) {
                if (this.logic.getBetFactor(symbolId) === this.logic.getTotalBet()) {
                    paytable = paytable.map((p) => p * this.logic.getWager());
                }

                symbolsData[symbolId] = {paytable, symbolSpriteSrc};
            }
        });

        return symbolsData;
    }

    protected getPayTable(symbolId: string): number[] {
        return this.logic.getSlotSymbol(Number(symbolId))?.paytable;
    }

    protected initData(): void {
        this.view.setViewData({
            symbolsData: this.getSymbolsData(this.getSymbolsMap()),
            wager: this._logic.getWager(),
            isWaysGame: this.logic.isWaysGame()
        });
    }

    private onInitialized(): void {
        if (this._view) {
            this.view.emit('UpfrontOddsReactView.MODULE_INITIALIZED');
            this.eventManager.emit('General.UI.DISABLE', OddsUpfrontModule.NAME);
        }
    }

    protected setupEvents(): void {
        super.setupEvents();

        this.on('General.Game.ODDS_UPFRONT_ACCEPTED', this.oddsAccepted.bind(this));
        this.onView('UpfrontOddsReactView.ACCEPT_CLICKED', this.onAcceptClicked.bind(this));
    }

    protected onAcceptClicked(): void {
        this.eventManager.emit('General.Game.ODDS_UPFRONT_ACCEPT');
        this.eventManager.emit('General.Game.FIRST_INTERACTION');
    }

    protected oddsAccepted(): void {
        this.view.acceptReceived();
        this.eventManager.emit('General.UI.ENABLE', OddsUpfrontModule.NAME);
        this.eventManager.emit('General.Game.START_GAME');
    }
}
